import { VITE_MY_EQUIP_BASE_URL } from './env';

const urlConstants = {
  assessments: {
    createAssessment: '/assessments',
    deleteAssessment: (responseExternalId: string): string =>
      `/surveys/delete/survey/${responseExternalId}`,
    getAssessmentById: (responseExternalId: string): string =>
      `/assessments/${responseExternalId}`,
    getAssessmentSummariesByInquiryId: (inquiryId: string): string =>
      `/assessments/inquiries/${inquiryId}`,
    getSafetyRiskAlerts: '/assessments/safety-risk-alerts',
    updateSafetyRiskAlert: (alertId: string): string =>
      `/assessments/safety-risk-alerts/${alertId}`,
    groupedAssessments: (patientId: string): string =>
      `/surveys/grouped-assessments/${patientId}`,
    groupedAssessmentsV2: (patientId: string): string =>
      `/surveys/v2/grouped-assessments/${patientId}`,
    listAssessments: '/assessments',
    listSurveys: '/surveys',
    sendEmail: '/assessments/send-email',
    updateAssessment: (responseExternalId: string): string =>
      `/assessments/${responseExternalId}`,
    updateAssessmentRespondent: (responseExternalId: string): string =>
      `/assessments/${responseExternalId}/edit-user`,
  },
  calendar: {
    auth: '/schedule/access-token',
    cancelAppointment: '/schedule/appointment',
    cancellationReasons: '/schedule/appointment/cancellation-reasons',
    cancelSection: (groupId: string, sectionExternalId: string): string =>
      `/schedule/v2/groups/${groupId}/sections/${sectionExternalId}`,
    createGroupClass: '/schedule/group-class',
    getAccount: '/schedule/user',
    getAccounts: '/schedule/users',
    getAppointmentDetails: '/schedule/appointment-details',
    getAppointmentDateTimeDetails: '/schedule/appointment-date-details',
    getLastJoinedAppointment: (patientId: string): string =>
      `/schedule/patients/${patientId}/appointments/last-joined`,
    getAppointmentDetailsById: (appointmentExternalId: string): string =>
      `/schedule/appointment/${appointmentExternalId}`,
    getAppointmentTypes: (name?: string): string =>
      `/schedule/appointment-types${name ? `?provider-type-name=${name}` : ''}`,
    getCronofyEventUids: (appointmentExternalId: string): string =>
      `/schedule/appointment-event-ids/${appointmentExternalId}`,
    getElementToken: '/schedule/management-element-token',
    getAttendees: '/schedule/attendees',
    getMeetingDetails: '/schedule/meeting',
    getNoteDetails: '/schedule/note',
    getProviderAvailability: '/schedule/availability',
    getProviderAvailabilityV2: '/schedule/v2/availability',
    getProxyElementToken: '/schedule/management-element-token/proxy',
    patientAndSupportsRegisteredGroupClassesList: (
      patientUserId: string,
    ): string => `/schedule/patients/${patientUserId}/groups`,
    patientAndSupportsRegisteredGroupClassesListV2: (
      patientUserId: string,
    ): string => `/schedule/v2/patients/${patientUserId}/groups`,
    recurringAppointments: (appointmentExternalId: string): string =>
      `/schedule/recurring-appointments/${appointmentExternalId}`,
    saveAppointment: '/schedule/appointment',
    batchSaveAppointments: '/schedule/appointments/batch',
    updateGroupClass: (groupClassExternalId: string): string =>
      `/schedule/group-class/${groupClassExternalId}`,
  },
  chat: {
    channelRotate: (channelUrl: string) => `/chat/channel/${channelUrl}/rotate`,
    chatMessage: `/chat/message`,
    getChatToken: `/chat/users/token`,
    inviteUserToChannel: '/chat/channel/invite',
    sendbirdProxy: {
      getGroupChannelMessageThread: (
        channel_url: string,
        prev_limit: number,
        next_limit: number,
        message_ts: number,
        parent_message_id: number,
      ): string =>
        `/chat/sendbird-proxy/group_channels/${channel_url}/messages?is_sdk=true&prev_limit=${prev_limit}&next_limit=${next_limit}&include=true&reverse=false&message_ts=${message_ts}&message_type=&include_reply_type=all&with_sorted_meta_array=false&include_reactions=true&parent_message_id=${parent_message_id}&include_thread_info=true&include_parent_message_info=true&include_poll_details=true`,
      getGroupChannelMessages: (
        channel_url: string,
        prev_limit: number,
        next_limit: number,
        message_ts: number,
      ): string =>
        `/chat/sendbird-proxy/group_channels/${channel_url}/messages?is_sdk=true&prev_limit=${prev_limit}&next_limit=${next_limit}&include=false&reverse=true&message_ts=${message_ts}&message_type=&include_reply_type=none&with_sorted_meta_array=false&include_reactions=true&include_thread_info=true&include_parent_message_info=true&show_subchannel_message_only=false&include_poll_details=true`,
      getGroupChannelsByPatientId: (patientId: string): string =>
        `/chat/sendbird-proxy/group_channels?token=&limit=50&order=chronological&show_member=true&show_read_receipt=true&show_delivery_receipt=true&show_empty=true&public_mode=all&super_mode=all&metadata_key=patientId&metadata_values=${patientId}&show_frozen=true&show_metadata=true`,
    },
  },
  clinical_data: {
    addBodyTraceOrders: `/clinical-data/bodytrace/orders`,
    addBodyTraceReorder: `/clinical-data/bodytrace/orders/reorder`,
    addDiagnosis: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/diagnoses`,
    getBodyTraceScaleReorderReasons:
      '/clinical-data/bodytrace/scale-reorder-reasons',
    createPatientBillingPeriod: (patientExternalId: string): string =>
      `/clinical-data/v2/patients/${patientExternalId}/billing-periods`,
    deleteBillingPeriod: (
      billingPeriodExternalId: string,
      patientExternalId: string,
    ) =>
      `/clinical-data/patients/${patientExternalId}/billing-periods/${billingPeriodExternalId}`,
    deleteGoal: (patientId: string, goalExternalId: string): string =>
      `/clinical-data/patients/${patientId}/clinical-profile/observations/goal/${goalExternalId}`,
    deleteNoteAttachment: (noteId: string, attachmentId: string): string =>
      `/clinical-data/notes/${noteId}/attachments/${attachmentId}`,
    deletePatientCoverageById: (coverageExternalId: string): string =>
      `/clinical-data/patients/delete/coverage/${coverageExternalId}`,
    deletePatientDocument: (
      documentExternalId: string,
      patientExternalId: string,
      voidReasonCode: string,
    ): string =>
      `/clinical-data/v2/patient-documents/${documentExternalId}?patientExternalId=${patientExternalId}&voidReasonCode=${voidReasonCode}`,
    discharge_plans: {
      addTask(dischargePlanId: string): string {
        return `/clinical-data/discharge-plans/${dischargePlanId}/tasks`;
      },
      getById(dischargePlanId: string): string {
        return `/clinical-data/discharge-plans/${dischargePlanId}`;
      },
      getTasks(dischargePlanId: string): string {
        return `/clinical-data/discharge-plans/${dischargePlanId}/tasks`;
      },
      updateDetails(dischargePlanId: string): string {
        return `/clinical-data/discharge-plans/${dischargePlanId}/details`;
      },
      updateStatus(dischargePlanId: string): string {
        return `/clinical-data/discharge-plans/${dischargePlanId}/status`;
      },
    },
    editDiagnosis: (patientId: string, externalId: string): string =>
      `/clinical-data/patients/${patientId}/diagnoses/${externalId}`,
    getActivitiesByPatientId: (patientExternalId: string): string =>
      `/clinical-data/patients/${patientExternalId}/activities`,
    getAigssByAppointmentId: (appointmentExternalId: string): string =>
      `/clinical-data/appointment/aigss/${appointmentExternalId}`,
    getBodyTraceOrders: (patientExternalId: string): string =>
      `clinical-data/bodytrace/orders/?patientExternalId=${patientExternalId}&orderBy=updated_at,created_at`,
    getNoteCommunicationMethods: '/clinical-data/notes/communication-methods',
    getActivityWith: '/clinical-data/notes/activity-with',
    getAdminEpisodesByPatientId: (patientExternalId: string): string =>
      `/clinical-data/patients/${patientExternalId}/episodes`,
    getAllPayorPrograms: '/clinical-data/payors/payor-programs',
    getAttachmentUploadUrl: `/clinical-data/patient-document`,
    getAttachmentVoidUrl: (documentUID: string): string =>
      `/clinical-data/patient-document/${documentUID}`,
    getBillingPeriodById: (billingPeriodId: string) =>
      `/clinical-data/v2/patients/billing-periods/${billingPeriodId}`,
    getBodyTraceDevices: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/bodytrace/devices`,
    getDiagnosisCodes: '/clinical-data/v2/diagnosis-codes',
    getDiagnosisCodesSearch: '/clinical-data/v2/diagnosis-codes/search',
    getDiagnosisStatuses: '/clinical-data/diagnosis-statuses',
    getDiagnosisStatusReasons: '/clinical-data/diagnosis-status-reasons',
    getDuplicateInquiryCoverages: (
      inquiryId: GetDuplicateCoveragesRequest['inquiryId'],
      coverageDate: GetDuplicateCoveragesRequest['coverageDate'],
      patientId: GetDuplicateCoveragesRequest['patientId'],
    ): string =>
      `/clinical-data/inquiries/${inquiryId}/coverages/duplicates?patient-external-id=${patientId}&active-coverage-date=${coverageDate}`,
    getEpisodeLevels: '/clinical-data/episode-levels',
    getEpisodeStatuses: '/clinical-data/episode-status',
    getAdminEpisodeStatusReasons: '/clinical-data/episode-status-reasons',
    getEquipPayorPayorPrograms: (equipPayorId: string): string =>
      `/clinical-data/equip-payors/${equipPayorId}/payor-programs`,
    getEquipPayors: (payorExternalId: string): string =>
      `/clinical-data/payors/${payorExternalId}/equip-payors`,
    getGroupClassEventNotes: '/clinical-data/group-class-event-notes',
    getGroupSectionEventNotes: '/clinical-data/group-section-event-notes',
    getInquiryNotesForPatient: '/clinical-data/v2/notes/',
    getInsuranceVerificationStatuses:
      '/clinical-data/insurance-verification-statuses',
    getMedianBMI: '/clinical-data/median-bmi',
    getMedicalClearanceStatuses: '/clinical-data/medical-clearance-statuses',
    getNoteAttachmentUploadURL: (noteId: string, fileName: string): string =>
      `/clinical-data/notes/${noteId}/attachments/${fileName}/upload-url`,
    getNotes: (patientId: string): string =>
      `/clinical-data/patient-chart/${patientId}/all-notes`,
    getNotesForPatient: '/clinical-data/notes/',
    getNoteTemplateAutopopulateData: (noteTemplateRevisionId: string) =>
      `/clinical-data/notes/templates/revisions/${noteTemplateRevisionId}/auto-populate`,
    getNoteTypes: '/clinical-data/notes/note-types',
    getObservationGoalFrequencies:
      '/clinical-data/patients/clinical-profile/observations/goal-frequencies',
    getObservationGoalMethods:
      '/clinical-data/patients/clinical-profile/observations/goal-methods',
    getObservationGoalStatuses:
      '/clinical-data/patients/clinical-profile/observations/goal-statuses',
    getObservationGoalTypes:
      '/clinical-data/patients/clinical-profile/observations/goal-types',
    getObservationInterpretations:
      '/clinical-data/patients/clinical-profile/observation-interpretations/',
    getObservations: (patientId: string): string =>
      `/composition/patients/${patientId}/observations`,
    getObservationsHistory: (observationId: string): string =>
      `/composition/patients/observations/history/${observationId}`,
    getObservationVoidReasons:
      '/clinical-data/patients/observations/void-reasons',
    getPatientBillingPeriods: (patientExternalId: string): string =>
      `/clinical-data/v2/patients/${patientExternalId}/billing-periods`,
    getPatientClinicalProfile: (patientId: string): string =>
      `/clinical-data/v2/patients/${patientId}/clinical-profile`,
    getPatientCoverages: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/coverages`,
    getPatientDiagnoses: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/diagnoses`,
    getPatientDocumentCategories: '/clinical-data/patient-document/categories',
    getPatientDocumentTags: '/clinical-data/v2/patient-documents/tags',
    getPatientDocumentDownloadURL: (
      patientId: string,
      fileName: string,
      documentExternalId: string,
    ): string =>
      `/clinical-data/patient-document/${patientId}/download-url/${fileName}?documentExternalId=${documentExternalId}`,
    getPatientDocuments: (patientId: string): string =>
      `/clinical-data/patient-document/${patientId}`,
    getPatientDocumentsV2: `/clinical-data/v2/patient-documents`,
    getPatientDocumentVoidReasons:
      '/clinical-data/v2/patient-documents/void-reasons',
    getPatientTreatmentEpisodes: (patientId: string): string =>
      `/clinical-data/v2/patients/${patientId}/treatment-episodes`,
    getPatientDocumentProviders: `/clinical-data/v2/patient-documents/providers`,
    getPayors: '/clinical-data/payors',
    getPrimaryDiagnosis: '/clinical-data/primary-diagnoses',
    getProviderTeamAssignments: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/all-provider-team-assignments`,
    updateProviderTeamPreliminaryAssignment: (inquiryId: string) =>
      `/clinical-data/inquiries/${inquiryId}/provider-team-assignments`,
    getPsychClearanceStatuses: '/clinical-data/psych-clearance-statuses',
    getStates: '/clinical-data/us-states',
    getSupportDetailsBySupportExternalId: (supportExternalId: string): string =>
      `/users/supports/${supportExternalId}`,
    getTreatmentEpisodeStatuses: '/clinical-data/treatment-episode-statuses',
    getTreatmentEpisodeStatusReasons:
      '/clinical-data/treatment-episode-status-reasons',
    getTreatmentPrograms: '/clinical-data/treatment-programs',
    getWeightGoals: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/observations/goal`,
    getTemplateRevisionById: (templateRevisionId: string): string =>
      `/clinical-data/notes/templates/revisions/${templateRevisionId}`,
    listTemplateRevision: '/clinical-data/notes/templates/revisions',
    listVoidReasons: '/clinical-data/notes/void-reasons',
    noteTemplates: '/clinical-data/notes/templates',
    patientRelationsToSubscriber:
      '/clinical-data/coverage-subscriber-relations-to-patient',
    postAiSummaryReview: '/clinical-data/ai-summary-review',
    postCoverage: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/coverages`,
    postAddendum: (noteExternalId: string) =>
      `/clinical-data/notes/${noteExternalId}/addenda`,
    getNote: (noteExternalId: string) =>
      `/clinical-data/notes/${noteExternalId}`,
    // TODO still some useApi calls to this endpoint, since we have a RTK query version now, we can remove those
    postNote: '/clinical-data/notes/',
    postGroupNote: '/clinical-data/group-class-event-notes',
    postGroupSectionEventNote: '/clinical-data/group-section-event-notes',
    putNote: '/clinical-data/notes/', // surprisingly, no id in url
    putNoteVoidReason: (noteId: string) =>
      `/clinical-data/notes/${noteId}/voidReason`,
    deleteNote: (noteId: string) => `/clinical-data/notes/${noteId}`,
    putGroupNote: (noteId: string) =>
      `/clinical-data/group-class-event-notes/${noteId}`,
    putGroupSectionEventNote: (noteId: string) =>
      `/clinical-data/group-section-event-notes/${noteId}`,
    // todo add deleteNotes?
    putCoverage: (patientId: string, coverageExternalId: string): string =>
      `/clinical-data/patients/${patientId}/coverages/${coverageExternalId}`,
    putPatientTreatmentEpisode: (
      patientId: string,
      treatmentEpisodeId: string,
    ): string =>
      `/clinical-data/v2/patients/${patientId}/treatment-episodes/${treatmentEpisodeId}`,
    ref_discharge_plans: {
      is_patient_seeking_cares:
        '/clinical-data/ref-discharge-plans/is-patient-seeking-cares',
      levels_of_care: '/clinical-data/ref-discharge-plans/levels-of-care',
      secondary_reasons: '/clinical-data/ref-discharge-plans/secondary-reasons',
      types_of_care: '/clinical-data/ref-discharge-plans/types-of-care',
      who_initiated_discharges:
        '/clinical-data/ref-discharge-plans/who-initiated-discharges',
    },
    saveGoal: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/clinical-profile/observations/goal`,
    saveObservation: (patientId: string): string =>
      `/clinical-data/patients/${patientId}/clinical-profile/observations`,
    treatment_summary: {
      create: (patientMrn: string): string =>
        `/clinical-data/v1/patients/${patientMrn}/treatment-summary`,
      validate: (patientMrn: string): string =>
        `/clinical-data/v1/patients/${patientMrn}/treatment-summary/validate`,
    },
    updateDocument: (documentExternalId: string): string =>
      `/clinical-data/v2/patient-documents/${documentExternalId}`,
    updateGoal: (patientId: string, goalExternalId: string): string =>
      `/clinical-data/patients/${patientId}/clinical-profile/observations/goal/${goalExternalId}`,
    updateObservation: (patientId: string, observationId: string): string =>
      `/clinical-data/patients/${patientId}/clinical-profile/observations/${observationId}`,
    updatePatientBillingPeriod: (
      patientExternalId: string,
      billingPeriodExternalId: string,
    ): string =>
      `/clinical-data/patients/${patientExternalId}/billing-period/${billingPeriodExternalId}`,
    updatePatientBillingPeriodV2: (
      patientExternalId: string,
      billingPeriodExternalId: string,
    ): string =>
      `/clinical-data/v2/patients/${patientExternalId}/billing-period/${billingPeriodExternalId}`,
    updateNoteTemplate: (templateId: string): string =>
      `/clinical-data/notes/templates/${templateId}`,
    addNoteTemplateRevision: (templateId: string): string =>
      `/clinical-data/notes/templates/${templateId}/revisions`,
    uploadPatientDocument: `/clinical-data/v2/patient-documents`,
  },
  providerDashboard: {
    getPatientsInView: (
      providerId: string,
      view: string,
      timezone?: string,
      date?: string,
    ): string =>
      `/composition/v1/provider/${providerId}/patients?view=${view}` +
      (timezone ? `&timezone=${encodeURIComponent(timezone)}` : '') +
      (date ? `&date=${date}` : ''),
    getAttendedPatientsCount: (providerId: string): string =>
      `/schedule/v2/appointments/dashboard-metrics/attended-patients?host=${encodeURIComponent(providerId)}`,
    getUnsignedNotes: (providerId: string): string =>
      `/clinical-data/notes/unsigned?provider=${encodeURIComponent(providerId)}`,
    getAlerts: (providerId: string): string =>
      `/composition/v1/provider/${providerId}/alerts`,
    updateAlertStatus: (providerId: string, alertId: string): string =>
      `/clinical-data/provider/${providerId}/alerts/${alertId}`,
  },
  groupClasses: {
    groupTypes: '/schedule/v2/group-types',
    addHosts: (groupClassEventExternalId: string): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}`,
    archiveGroupClass: (groupClassExternalId: string): string =>
      `/schedule/archive/${groupClassExternalId}`,
    archiveReasons: '/schedule/archive-reasons?archive-type=UI',
    cancelGroupClassEvent: (groupClassEventExternalId: string): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}/cancel`,
    createGroupV2Url: '/schedule/v2/groups',
    getMySectionsListUrl: '/schedule/v2/sections',
    editGroupV2Url: (groupId: string): string =>
      `/schedule/v2/groups/${groupId}`,
    declineGroupClassEvent: (groupClassEventExternalId: string): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}/decline`,
    getActiveOrArchiveGroupClasses: '/schedule/groups',
    getActiveOrArchiveGroupsV2: '/schedule/v2/groups',
    getGroupClassEvents: '/schedule/group-class-events',
    getGroupEventsV2: '/schedule/v2/group-section-events',
    getGroupSectionEventDates: '/schedule/v2/group-section-events-dates',
    getGroupSectionEventDetails: (
      groupSectionEventExternalId: string,
    ): string =>
      `/schedule/v2/group-section-events/${groupSectionEventExternalId}`,
    getGroupClass: (groupClassExternalId: string): string =>
      `/schedule/group-classes/${groupClassExternalId}`,
    getGroupClassCount: '/schedule/group-counts',
    getGroupClassCountV2: '/schedule/v2/groups-count',
    getGroupClassEventConfirmedAttendees: (
      groupClassEventExternalId: string,
    ): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}/confirmed-attendees`,
    getGroupClassEventDetails: (groupClassEventExternalId: string): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}`,
    getGroupClassEventMeetingDetails: (
      groupClassEventExternalId: string,
    ): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}/start`,
    getGroupSectionEventMeetingDetailsV2: (
      groupSectionEventExternalId: string,
    ): string =>
      `/schedule/v2/group-section-event/${groupSectionEventExternalId}/start`,
    getYourGroupClasses: '/schedule/my-groups',
    getStatusOptions: '/schedule/v2/group-statuses',
    getGroupDetailsV2: (groupClassExternalId: string): string =>
      `/schedule/v2/groups/${groupClassExternalId}`,
    updateGroupStatus: (groupId: string): string =>
      `/schedule/v2/groups/${groupId}/status`,
    getFrequencyOptions: '/schedule/v2/group-section-frequencies',
    getSessionOptions: '/schedule/v2/group-section-sessions',
    createGroupSession: (groupId: string): string =>
      `/schedule/v2/groups/${groupId}/sections`,
    editGroupSession: (groupId: string, sectionId: string): string =>
      `/schedule/v2/groups/${groupId}/sections/${sectionId}`,
    getGroupSessionDetails: (groupId: string, sectionId: string): string =>
      `/schedule/v2/groups/${groupId}/sections/${sectionId}`,
    getAllGroupSessions: (groupId: string): string =>
      `/schedule/v2/groups/${groupId}/sections`,
    getGroupEventRegistrants: (groupSectionEventExternalId: string): string =>
      `/schedule/v2/group-section-event/${groupSectionEventExternalId}/attendees`,
    modifyGroupClassEventAttendees: (
      groupClassEventExternalId: string,
    ): string =>
      `/schedule/group-class-events/${groupClassEventExternalId}/modify-attendees`,

    modifyGroupSectionEventAttendees: (
      groupSectionEventExternalId: string,
    ): string =>
      `/schedule/group-section-events/${groupSectionEventExternalId}/modify-attendees`,
    registerGroupClass: (groupClassExternalId: string): string =>
      `/schedule/group-classes/${groupClassExternalId}/register`,
    registerPatientOrSupportForGroupClass: (
      groupClassExternalId: string,
      userId: string,
    ): string =>
      `/schedule/groups/${groupClassExternalId}/register-user/${userId}`,
    updateGroupSectionRegistrationForPatient: (
      patientId: string,
      groupSectionId: string,
    ): string =>
      `/schedule/v2/patients/${patientId}/group-section-registrations/${groupSectionId}`,
    unRegisterGroupClass: (groupClassExternalId: string): string =>
      `/schedule/group-classes/${groupClassExternalId}/unregister`,
    unRegisterPatientOrSupportForGroup: (
      groupClassExternalId: string,
      userId: string,
    ): string =>
      `/schedule/groups/${groupClassExternalId}/registered-users/${userId}`,
    updateGroupSectionEventV2: (groupEventExternalId: string): string =>
      `/schedule/v2/group-section-event/${groupEventExternalId}`,
  },
  inquiry: {
    closedReasons: '/inquiry/closed-reasons',
    confirmPatient: (inquiryId: string): string =>
      `/inquiry/inquiries/${inquiryId}/confirm-patient`,
    coverageReviewStatuses: '/inquiry/coverage-review-statuses',
    coverageReviewTypes: '/inquiry/coverage-review-types',
    createInquiry: '/inquiry/v2/inquiries',
    enroll: (inquiryId: string) => `/inquiry/v2/inquiries/${inquiryId}/enroll`,
    getInquiries: '/composition/inquiries',
    getInquiryViews: '/composition/inquiry-views',
    getInquiryViewsByCode: (viewCode: string) =>
      `/composition/inquiry-views/${viewCode}`,
    getInquiryViewsSchema: (viewCode: string) =>
      `/composition/inquiry-views/${viewCode}/schema`,
    hearAboutEquipSourcesV2: '/inquiry/equip-sources',
    informationGatheredSourcesV2: '/inquiry/information-sources',
    inquirerRoles: '/inquiry/inquirer-roles',
    inquiryAssignment: (inquiryId: string, assignmentId: string): string =>
      `/inquiry/inquiries/${inquiryId}/assignments/${assignmentId}`,
    inquiryAssignments: (inquiryId: string): string =>
      `/inquiry/inquiries/${inquiryId}/assignments`,
    inquiryDetail: (inquiryId: string): string =>
      `/inquiry/inquiries/${inquiryId}`,
    inquiryDetailV2: (inquiryId: string): string =>
      `/inquiry/v2/inquiries/${inquiryId}`,
    inquiryInsuranceReview: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/reviews/insurance`,
    inquiryInsuranceReviewStatus: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/reviews/insurance/status`,
    inquiryListItemById: (inquiryId: string, viewCode: string): string =>
      `/composition/inquiries/${inquiryId}?view-code=${viewCode}`,
    inquiryMedicalReview: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/reviews/medical`,
    inquiryMedicalReviewStatus: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/reviews/medical/status`,
    inquiryNextContactDate: (inquiryId: string): string =>
      `/inquiry/inquiries/${inquiryId}/next-contact-date`,
    inquiryNextSteps: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/next-steps`,
    inquiryReviews: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/reviews`,
    inquiryStatus: (inquiryId: string): string =>
      `/inquiry/inquiries/${inquiryId}/status`,
    inquiryStatuses: '/inquiry/inquiry-statuses',
    inquiryTargetAdmissionDate: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/target-admission-date`,
    medicalReviewNotClearedReasons:
      '/inquiry/medical-review-not-cleared-reasons',
    medicalReviewStatuses: '/inquiry/medical-review-statuses',
    medicalReviewTypes: '/inquiry/medical-review-types',
    mergeDuplicateContactsAndCoverages: (inquiryId: string) =>
      `/inquiry/inquiries/${inquiryId}/merge`,
    patientInquiries: (patientId: string) =>
      `/inquiry/inquiries/patients/${patientId}`,
    patientInquirySummary: (patientId: string) =>
      `/inquiry/summaries/patients/${patientId}`,
  },
  integration: {
    getIntercomMaudJWT: `/integration-service/intercom/token`,
    getScriptSurePrescribingURL: (patientId: string): string =>
      `/integration-service/scriptsure/prescribing-url?patient-id=${patientId}`,
    getScriptSurePrescriptionMessagesCount: `/integration-service/scriptsure/prescription-messages/count`,
    getScriptSurePrescriptionMessagesURL: `/integration-service/scriptsure/prescription-messages/iframe-url`,
    getScriptSureSyncFailures: `/integration-service/scriptsure/sync-failures`,
    reSyncScriptSurePatient: (patientId: string): string =>
      `/integration-service/scriptsure/patients/${patientId}/resync`,
  },
  labs: {
    getLabNetwork: (patientId: string): string =>
      `/clinical-data/health-gorilla/lab-network-url/${patientId}`,
    getPatientChartUrl: (patientId: string): string =>
      `/clinical-data/health-gorilla/patient-chart-url/${patientId}`,
  },
  mai: {
    feedback: '/mai/feedback',
    messages: '/mai/messages',
    prompts: '/mai/prompts',
    retractFeedback: (messageId: string): string =>
      `/mai/feedback/${messageId}`,
  },
  myEquip: {
    survey: (responseExternalId: string): string =>
      `${VITE_MY_EQUIP_BASE_URL}/survey/${responseExternalId}`,
  },
  notifications: {
    getNotifications: '/notifications',
    updateNotification: '/notifications',
  },

  patients: {
    addCareTeam: '/users/care-team',
    addPatient: '/users/v2/patients',
    addPatientContactById: (patientId: string): string =>
      `/users/patients/patient-contact/${patientId}`,
    createPatientAccount: (patientId: string): string =>
      `/users/patients/${patientId}/account`,
    deletePatientContactById: (contactId: string, providerId: string): string =>
      `/users/patients/patient-contact/${contactId}?provider-id=${providerId}`,
    deletePatientSupportLinkById: (
      supportId: string,
      patientId: string,
    ): string =>
      `/users/supports/${supportId}/patient-link?patient-external-id=${patientId}`,
    editPatientContactById: (contactId: string): string =>
      `/users/patients/patient-contact/${contactId}`,
    getActivitiesByPatientId: (patientExternalId: string): string =>
      `/users/patients/${patientExternalId}/activities`,
    getCareTeams: (patientId: string): string =>
      `/users/care-team/${patientId}`,
    getDuplicateContacts: (inquiryId: string, patientId: string): string =>
      `/users/inquiries/${inquiryId}/contacts/duplicates?patient-external-id=${patientId}`,
    getGroupsCount: (patientExternalId: string): string =>
      `/schedule/patients/${patientExternalId}/groups-count`,
    getGroupsCountV2: (patientExternalId: string): string =>
      `/schedule/v2/patients/${patientExternalId}/groups-count`,
    getLinkedPatientsForEmail: (email: string) =>
      `/users/supports/linked-patients?email=${encodeURIComponent(email)}`,
    getPatientAndSupportAvailableGroupsById: (patientId: string): string =>
      `/schedule/patients/${patientId}/available-groups`,
    getPatientAndSupportAvailableGroupsByIdV2: (patientId: string): string =>
      `/schedule/v2/patients/${patientId}/available-groups`,
    getPatientById: (
      patientId: string,
      includeLinkedUsers: boolean = false,
    ): string =>
      `/users/patients/${patientId}${
        includeLinkedUsers ? `?includeLinkedUsers=${includeLinkedUsers}` : ''
      }`,
    getPatientContactById: (patientId: string): string =>
      `/users/patients/patient-contact/${patientId}`,
    getPatientContactsByInquiryId: (inquiryId: string): string =>
      `/users/v2/inquiries/${inquiryId}/contacts`,
    getPatientTasksAppUsers: (patientId: string): string =>
      `/users/patients/${patientId}/app-users`,
    getSupportForEmail: (email: string): string =>
      `/users/supports?email=${email}`,
    invitePatient: (patientId: string): string =>
      `/users/patients/${patientId}/invite`,
    inviteProvider: (providerId: string): string =>
      `/users/providers/${providerId}/invite`,
    inviteSupport: (supportId: string): string =>
      `/users/supports/${supportId}/invite`,
    linkPatientToExistingSupport: (supportExternalId: string): string =>
      `/users/supports/${supportExternalId}/patient-link`,
    patientListV5: '/composition/v5/patients',
    putBasicInfo: (patientId: string): string => `/users/patients/${patientId}`,
    putTagsInfo: (patientId: string): string => `/users/${patientId}/tags`,
    setActive: (patientExternalId: string): string =>
      `/users/patients/${patientExternalId}/is-active`,
    autoRegisterPatient: (patientId: string): string =>
      `/schedule/patients/${patientId}/supports/registration`,
  },
  providers: {
    outOfOffice: '/users/providers/outOfOffice',
  },
  reports: {
    getPatientReportLink: (patientId: string): string =>
      `/users/reports/INDV_PT/patient/${patientId}`,
    getPatientNoteSummaryDashboardReportLink: (patientId: string): string =>
      `/users/reports/NOTE_SUM_DASH/patient/${patientId}`,
    getPatientInsightsDashboardReportLink: (patientId: string): string =>
      `/users/reports/PT_INS_DASH/patient/${patientId}`,
    getUserReportLink: (reportId: string): string =>
      `/users/reports/${reportId}`,
    getUserReports: '/users/reports',
  },
  scribe: {
    postEvent: '/scribe',
  },
  tasks: {
    activity: '/tasks/activity',
    activityByTaskId: (taskExternalId: string): string =>
      `/composition/tasks/activity/${taskExternalId}`,
    categories: '/tasks/categories',
    comments: '/tasks/comment',
    commentsByTaskId: (taskExternalId: string): string =>
      `/tasks/comment/${taskExternalId}`,
    countForUserId: (userId: string): string => `/tasks/count/${userId}`,
    getSummaries: '/tasks',
    listTasks: '/composition/tasks',
    listVoidReasons: '/tasks/void-reason',
    post: '/tasks',
    put: '/tasks',
  },
  techOps: {
    bulkProviderTeamAssignments:
      '/clinical-data/ops/bulk/provider-team-assignments',
    bulkProviderTeamAssignmentsByJobKey: (jobKey: string): string =>
      `/clinical-data/ops/bulk/provider-team-assignments/${jobKey}`,
    bulkProviderTeamAssignmentsLatestJob:
      '/clinical-data/ops/bulk/provider-team-assignments/latest-job',
  },
  treatmentAttributes: {
    createAttributes: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/attributes`,
    getAttributes: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/attributes`,
    getEpisodeDiscussionOfWeights: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/discussion-of-weights`,
    getEpisodeForm: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/forms`,
    getEpisodeLevels: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/levels`,
    getEpisodeMedicalStabilities: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/medical-stabilities`,
    getEpisodeModality: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/modalities`,
    getEpisodePhaseStages: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/phase-stages`,
    getEpisodePsychiatricStabilities: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/psychiatric-stabilities`,
    getEpisodeRefeedingRisks: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/refeeding-risks`,
    getEpisodeSaferStages: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/safer-stages`,
    getMedicalStabilities: '/clinical-data/treatment-medical-stabilities',
    getPsychiatricStabilities:
      '/clinical-data/treatment-psychiatric-stabilities',
    getRefeedingRisks: '/clinical-data/treatment-refeeding-risks',
    getSaferStages: '/clinical-data/treatment-safer-stages',
    getTreatmentDiscussionsOfWeight:
      '/clinical-data/treatment-discussion-of-weight',
    getTreatmentModalities: '/clinical-data/treatment-modalities',
  },
  treatmentEpisodes: {
    addProviderTeamAssignment: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/provider-team-assignments`,
    deleteProviderTeamAssignment: (
      treatmentEpisodeId: string,
      assignmentId: string,
    ): string =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/provider-team-assignments/${assignmentId}`,
    getVoidReasons: '/clinical-data/provider-team-void-reasons',
  },
  carePlans: {
    createCarePlan: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/care-plans`,
    updateCarePlan: (treatmentEpisodeId: string, carePlanId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/care-plans/${carePlanId}`,
    getCarePlans: (treatmentEpisodeId: string) =>
      `/clinical-data/treatment-episodes/${treatmentEpisodeId}/care-plans`,
    getCarePlanForNote: (noteId: string) =>
      `/clinical-data/notes/${noteId}/care-plan-template`,
    createProblem: (treatmentPathwayId: string) =>
      `/clinical-data/care-plans/v2/${treatmentPathwayId}/problems/`,
    createMultipleProblems: (carePlanId: string) =>
      `/clinical-data/care-plans/${carePlanId}/problems`,
    updateProblem: (problemId: string) =>
      `/clinical-data/care-plan-problems/${problemId}`,
    deleteProblem: (problemId: string) =>
      `/clinical-data/care-plan-problems/${problemId}`,
    updateManifestation: (manifestationId: string) =>
      `/clinical-data/care-plan-manifestations/${manifestationId}`,
    createQoLGoal: (carePlanId: string) =>
      `/clinical-data/care-plans/${carePlanId}/qol-goals`,
    updateQoLGoal: (goalId: string) =>
      `/clinical-data/care-plan-qol-goals/${goalId}`,
    deleteQolGoal: (goalId: string) =>
      `/clinical-data/care-plan-qol-goals/${goalId}`,
    updateGoal: (goalId: string) => `/clinical-data/care-plan-goals/${goalId}`,
    revertGoalStatusOnNote: (goalId: string, noteExternalId: string) =>
      `/clinical-data/care-plan-goals/${goalId}/notes/${noteExternalId}/revert-status`,
    revertQoLGoalStatusOnNote: (goalId: string, noteExternalId: string) =>
      `/clinical-data/care-plan-qol-goals/${goalId}/notes/${noteExternalId}/revert-status`,
    updateIntervention: (interventionId: string) =>
      `/clinical-data/care-plans-interventions/${interventionId}`,
    createCustomInterventions: '/clinical-data/care-plans-custom-interventions',
    voidCustomIntervention: (interventionId: string) =>
      `/clinical-data/care-plans-custom-interventions/${interventionId}/void`,
    // Ref data endpoints
    getGoalStatusesEnum: '/clinical-data/ref-care-plans/goal-statuses',
    getProblemTypesGoalStatusesMap:
      '/clinical-data/ref-care-plans/problem-types-goal-statuses-map',
    getInterventionVoidReasons:
      '/clinical-data/ref-care-plans/intervention-void-reasons',
    getRefCarePlanProblemsEnum: (diagnosisCodeLabels?: string[]) =>
      `/clinical-data/ref-care-plans/problems${diagnosisCodeLabels ? '?diagnosis-code-labels=' + diagnosisCodeLabels.join(',') : ''}`,
    getRefCarePlanGoalsEnum: (problemCodes?: string[]) =>
      `/clinical-data/ref-care-plans/goals${problemCodes ? '?problem-code=' + problemCodes.join(',') : ''}`,
    getRefCareInterventionsEnum: (
      goalCodes?: string[],
      modalityCode?: string,
    ) => {
      const queryParams = new URLSearchParams();
      if (goalCodes) {
        queryParams.append('goal-code', goalCodes.join(','));
      }
      if (modalityCode) {
        queryParams.append('modality-code', modalityCode);
      }
      return `/clinical-data/ref-care-plans/interventions${queryParams.size > 0 ? '?' + queryParams.toString() : ''}`;
    },
    getRefCareManifestationsEnum: (problemCodes?: string[]) =>
      `/clinical-data/ref-care-plans/manifestations${problemCodes ? '?problem-code=' + problemCodes.join(',') : ''}`,
    getFrequencies: '/clinical-data/ref-care-plans/frequencies',
    getInterventionCategories:
      '/clinical-data/ref-care-plans/intervention-categories',
    getInterventionCategoriesAssigneesMap:
      '/clinical-data/ref-care-plans/intervention-categories-assignees-map',
    getProviderTypeManifest:
      '/clinical-data/ref-care-plans/provider-type-manifest',
  },
  user_profile: {
    addOutOfOffice: (providerExternalId: string): string =>
      `/users/providers/${providerExternalId}/outOfOffice`,
    deleteOutOfOffice: (providerExternalId: string, oooId: string): string =>
      `/users/providers/${providerExternalId}/outOfOffice/${oooId}`,
    deleteUserAvatarById: (providerExternalId: string): string =>
      `/users/providers/${providerExternalId}/avatar`,
    editOutOfOffice: (providerExternalId: string, oooId: string): string =>
      `/users/providers/${providerExternalId}/outOfOffice/${oooId}`,
    editUserAvatarById: (providerExternalId: string): string =>
      `/users/providers/${providerExternalId}/avatar`,
    getAvatarById: (providerExternalId: string): string =>
      `/users/providers/${providerExternalId}/avatar`,
    getOutOfOfficeList: (
      providerExternalId: string,
      startDate: string,
    ): string =>
      `/users/providers/${providerExternalId}/outOfOffice?fromStartDate=${startDate}`,
  },
  users: {
    addUser: '/users/v2/providers',
    editAboutMeById: (providerExternalId: string): string =>
      `/users/provider-details/${providerExternalId}`,
    editAdminUserById: (providerExternalId: string): string =>
      `users/v2/providers/${providerExternalId}`,
    editPermissionsById: (providerExternalId: string): string =>
      `/users/v2/providers/${providerExternalId}`,
    editSupportById: (supportId: string): string =>
      `/users/supports/${supportId}`,
    editUserById: (providerExternalId: string): string =>
      `/users/provider-details/${providerExternalId}`,

    getPatients: '/users/patients',
    getProviderInfoWithHistoryById: (
      providerExternalId: string,
      includeProviderHistory: boolean,
      includeAvatar: boolean,
      includeOutOfOffice: boolean,
      provideAuthDetails?: boolean,
    ): string => {
      const params = new URLSearchParams({
        'include-provider-history': String(includeProviderHistory),
        'include-avatar': String(includeAvatar),
        'include-out-of-office': String(includeOutOfOffice),
        'include-auth-details': String(provideAuthDetails),
      });

      return `/users/v2/providers/${providerExternalId}?${params.toString()}`;
    },
    getTimezoneFromLatAndLong: `/users/timezone-details`,
    getUniversalStatuses: '/users/enums/universal-statuses',
    listUserV2: '/users/v2/providers',
    logout: '/users/logout',
    matchPatients: '/users/patients/match-patient',
    supports: '/users/supports',
    userById: (
      providerExternalId: string,
      includeAuthDetails?: boolean,
    ): string =>
      `/users/providers/${providerExternalId}${
        includeAuthDetails ? '?auth-details=true' : ''
      }`,
    users: '/users',
  },
  reassignments: {
    createReassignments: '/clinical-data/reassignments',
    getCandidatePatients: (providerId: string): string =>
      `/clinical-data/reassignments/patients?provider-id=${providerId}`,
    getReassignmentReasons: '/clinical-data/reassignments/reasons',
    getReassignments: (patientId: string): string =>
      `/clinical-data/reassignments?patient-id=${patientId}`,
    updateReassignment: (reassignmentId: string): string =>
      `/clinical-data/reassignments/${reassignmentId}`,
    deleteReassignment: (reassignmentId: string): string =>
      `/clinical-data/reassignments/${reassignmentId}`,
  },
  iq: {
    getProviderAssignmentRecommendations: (patientId: string): string =>
      `/assignment/patients/${patientId}`,
  },
};

export default urlConstants;
